import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { finalize, first, takeUntil } from 'rxjs/operators';
import { TicketService } from '../../core/services/ticket/ticket.service';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { NotificationsService } from 'angular2-notifications';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, timer } from 'rxjs';
import { ConfirmationModalComponent } from '../../shared/modal-windows/confirmation-modal/confirmation-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { WindowRef } from '@sb-tools/typescript/services/window-ref.service';
import { PrintTicketService } from '../../core/services/ticket/print-ticket.service';
import { RightPanelType } from '../../core/enums/right-panel-type.enum';
import { RightPanelService } from '../../core/services/right-panel.service';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { TicketView } from '../../core/enums/ticket-view.enum';
import { ForwardMessageService } from '../../core/services/forward-message.service';
import { ThreadFilterService } from '../../core/services/thread-filter.service';
import { ForwardEntityTypeEnum } from '../../core/enums/forward-entity-type.enum';
import { ReminderModalComponent } from '../../shared/modal-windows/reminder-modal/reminder-modal.component';
import { TicketReminderDTO } from '@sb-tools/typescript/models/dto/ticket-reminder.dto';
import { TicketDetailsComponent } from '../ticket-details.component';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { NgClass } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ReminderMenuComponent } from '../../shared/components/reminder-menu/reminder-menu.component';
import { HidePostButtonComponent } from '../hide-post-button/hide-post-button.component';
import { isInboxElementVisible } from '../../core/utils/is-inbox-element-visible.util';

@Component({
    selector: 'app-ticket-toolbar',
    templateUrl: './ticket-toolbar.component.html',
    styleUrls: ['./ticket-toolbar.component.scss'],
    imports: [
        NgClass,
        TooltipModule,
        InlineSVGComponent,
        BsDropdownModule,
        ReminderMenuComponent,
        HidePostButtonComponent,
        TranslateModule,
    ],
})
export class TicketToolbarComponent implements OnInit, OnChanges, OnDestroy {
    @Output() markTicketAsSpam = new EventEmitter<void>();
    @Output() showFullReplyFormEvent = new EventEmitter<void>();

    @Input() isCustomerOnline: boolean;
    @Input() isSocialMediaTicket: boolean;
    @Input() isUpdatingSpam: boolean;
    @Input() ticket: DetailedTicketDTO;
    @Input() ticketViewType: TicketView;

    isAnonymizingThread = false;
    isMuteStateUpdating = false;
    isPingingTeammates: boolean;
    isReopenTicket = false;
    isSavingReminder = false;
    isSugarCrmAndTrackingAvailable: boolean;
    postnordLink: string;
    sugarCrmLink: string;
    threadFilterService = ThreadFilterService;
    ticketLink: string;
    ticketSourceEnum = TicketSource;
    trackingLink: string;

    private forwardEntityTypeEnum = ForwardEntityTypeEnum;
    private unsubscriber = new Subject<void>();

    constructor(
        @Inject(TicketDetailsComponent) private parent: TicketDetailsComponent,
        private ticketService: TicketService,
        public commonDataService: CommonDataService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private bsModalService: BsModalService,
        private windowRef: WindowRef,
        private printTicketService: PrintTicketService,
        private rightPanelService: RightPanelService,
        private forwardMessageService: ForwardMessageService,
    ) {}

    ngOnInit(): void {
        this.isSugarCrmAndTrackingAvailable = ['demo', 'postnord'].includes(this.commonDataService.community);
    }

    ngOnChanges(changes: SimpleChanges) {
        // todo: remove + when ticket will not have id as string
        if (changes.ticket) {
            if (
                changes.ticket.currentValue?.id &&
                +changes.ticket.currentValue?.id !== +changes.ticket.previousValue?.id
            ) {
                this.ticketLink =
                    this.windowRef.nativeWindow.location.origin +
                    '/' +
                    this.commonDataService.communityName +
                    '/tickets?ticketId=' +
                    this.ticket.id;
            }

            if (changes.ticket.currentValue.fields && this.isSugarCrmAndTrackingAvailable) {
                this.sugarCrmLink = `https://sugarcrm.postnord.no/index.php?entryPoint=casenew&session_id=${this.ticket.id}&agent=${this.commonDataService.user.email}`;
                const trackingCodeField = this.ticket.fields.find((element) => {
                    return element.name === 'Sendingsnummer';
                });
                this.postnordLink = trackingCodeField
                    ? `https://portal.postnord.no/tracking?query=${trackingCodeField.value}`
                    : null;
                this.trackingLink = trackingCodeField
                    ? `http://tgapp.postnord.no:8080/sending/#/detaljer/${trackingCodeField.value}`
                    : null;
            }

            this.isReopenTicket = Boolean(changes.ticket.currentValue?.reminder?.isSetTicketStatusToOpenAfterRemind);
        }
    }

    addReminder(reminder: TicketReminderDTO): void {
        this.isSavingReminder = true;

        this.ticketService
            .addReminder(this.ticket.id, reminder)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSavingReminder = false;
                }),
            )
            .subscribe((addedReminder: TicketReminderDTO) => {
                this.parent.ticket.reminder = addedReminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.isReopenTicket = addedReminder.isSetTicketStatusToOpenAfterRemind;
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully added.'),
                );
            });
    }

    anonymizeThread() {
        this.isAnonymizingThread = true;

        this.ticketService
            .anonymize(this.ticket.id)
            .pipe(
                finalize(() => {
                    this.isAnonymizingThread = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((ticket) => {
                this.parent.handleUpdatedTicketFromAction(ticket);
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('This ticket thread was anonymized.'),
                );
            });
    }

    deleteReminder(): void {
        this.isSavingReminder = true;

        this.ticketService
            .deleteReminder(this.ticket.id)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSavingReminder = false;
                }),
            )
            .subscribe(() => {
                this.parent.ticket.reminder = null;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully removed.'),
                );
            });
    }

    muteTicket() {
        this.isMuteStateUpdating = true;

        const newMuteState = !this.ticket.isMuted;
        this.ticketService
            .changeMuteState(this.ticket.id, newMuteState)
            .pipe(
                finalize(() => {
                    this.isMuteStateUpdating = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe(() => {
                this.ticket.isMuted = newMuteState;
            });
    }

    onIsReopenClick(isReopen: boolean): void {
        if (!this.parent.ticket.reminder) {
            timer(0).subscribe(() => {
                this.isReopenTicket = isReopen;
            });
            return;
        }

        const reminder: TicketReminderDTO = {
            isSetTicketStatusToOpenAfterRemind: isReopen,
            time: this.parent.ticket.reminder.time,
        };

        this.addReminder(reminder);
    }

    onRemindClick(minutes: number): void {
        const dateTime = new Date(new Date().getTime() + 60000 * minutes);

        const reminder: TicketReminderDTO = {
            isSetTicketStatusToOpenAfterRemind: this.isReopenTicket,
            time: dateTime,
        };

        this.addReminder(reminder);
    }

    openAnonymizeThreadModal() {
        const initialState = {
            title: this.translateService.instant('Please confirm that you want to make this ticket fully anonymous.'),
            confirmText: this.translateService.instant('Yes, make this ticket anonymous'),
            declineText: this.translateService.instant('No'),
            confirmCallback: () => {
                return this.anonymizeThread();
            },
        };

        this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });
    }

    openDeletePostModal() {
        const initialState = {
            // tslint:disable-next-line:max-line-length
            title: this.translateService.instant(
                'Are you sure you want to delete this facebook post from your wall? This action can not be un-done.',
            ),
            confirmText: this.translateService.instant('Yes, delete the post'),
            declineText: this.translateService.instant("No, don't do anything"),
            confirmCallback: () => {
                return this.deletePost();
            },
        };

        this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });
    }

    openForwardForm() {
        this.forwardMessageService.openForwardForm(this.ticket, this.forwardEntityTypeEnum.ticket, this.ticket);
    }

    openMarkAsSpamModal() {
        let modalTitle: string;

        if (this.ticket.isSpam) {
            modalTitle = this.translateService.instant('Are you sure you want to restore this ticket from spam?');
        } else {
            if (this.isSocialMediaTicket) {
                modalTitle = this.translateService.instant('Are you sure you want to move this ticket to spam?');
            } else {
                modalTitle = this.translateService.instant(
                    'Are you sure you want to move this ticket to spam and mark user as spammer?',
                );
            }
        }

        const initialState = {
            title: modalTitle,
            confirmText: this.translateService.instant('Yes'),
            declineText: this.translateService.instant('Cancel'),
            confirmCallback: () => {
                return this.markTicketAsSpam.emit();
            },
        };

        this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });
    }

    openRedactForm() {
        this.rightPanelService.open(RightPanelType.redactTicket, {
            ticket: this.ticket,
        });
    }

    openReminderModal(): void {
        const initialState = {
            ticketId: this.ticket.id,
            confirmCallback: (reminder: TicketReminderDTO) => {
                this.parent.ticket.reminder = reminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.isReopenTicket = reminder.isSetTicketStatusToOpenAfterRemind;
            },
        };

        this.bsModalService.show(ReminderModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    pingTeammates() {
        this.isPingingTeammates = true;

        this.ticketService
            .pingTeammates(this.ticket.id)
            .pipe(
                finalize(() => {
                    this.isPingingTeammates = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe();
    }

    printTicket() {
        this.threadFilterService.messageThreadForPrinting = JSON.parse(JSON.stringify(this.ticket.messageThread));

        setTimeout(() => {
            this.threadFilterService.isPrinting = true;

            this.printTicketService
                .print()
                .pipe(first())
                .subscribe(() => {
                    this.threadFilterService.isPrinting = false;
                });
        }, 1);
    }

    showFullReplyForm() {
        this.showFullReplyFormEvent.emit();
    }

    isTicketHeaderVisible(
        key:
            | 'anonymizeAction'
            | 'spamAction'
            | 'forwardAction'
            | 'redactAction'
            | 'openInNewTabAction'
            | 'remindAction'
            | 'printAction',
    ): boolean {
        return isInboxElementVisible(
            this.commonDataService.customerSettings?.inboxElementsVisibility?.ticketHeader?.[key],
        );
    }

    isMoreActionsVisible(): boolean {
        return (
            this.ticket.constraints?.isHidingAllowed ||
            this.ticket.constraints?.isDeletionAllowed ||
            this.isTicketHeaderVisible('printAction')
        );
    }

    private deletePost() {
        this.ticketService
            .deletePost(this.ticket.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.ticket.isEditable = false;
            });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
